import React, { useState, useEffect } from 'react';
import './PhotoCarousel.css';

interface PhotoCarouselProps {
  photos: Array<{ url: string; id?: number }>;
  height?: number;
  autoPlay?: boolean;
  interval?: number;
  showIndicators?: boolean;
  onImagePress?: (url: string) => void;
}

const PhotoCarousel: React.FC<PhotoCarouselProps> = ({
  photos,
  height = 300,
  autoPlay = false,
  interval = 3000,
  showIndicators = true,
  onImagePress,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-play functionality
  useEffect(() => {
    if (!autoPlay || photos.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % photos.length);
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, interval, photos.length]);

  // Handle touch/swipe events
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    const startX = touch.clientX;
    
    const handleTouchEnd = (e: TouchEvent) => {
      const touch = e.changedTouches[0];
      const endX = touch.clientX;
      const diffX = startX - endX;
      
      if (Math.abs(diffX) > 50) { // Minimum swipe distance
        if (diffX > 0) {
          // Swipe left - next photo
          setCurrentIndex((prevIndex) => (prevIndex + 1) % photos.length);
        } else {
          // Swipe right - previous photo
          setCurrentIndex((prevIndex) => (prevIndex - 1 + photos.length) % photos.length);
        }
      }
    };
    
    document.addEventListener('touchend', handleTouchEnd, { once: true });
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + photos.length) % photos.length);
      } else if (e.key === 'ArrowRight') {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % photos.length);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [photos.length]);

  if (!photos || photos.length === 0) {
    return (
      <div className="photo-carousel empty" style={{ height }}>
        <div className="carousel-placeholder">
          <p>No photos available</p>
        </div>
      </div>
    );
  }

  if (photos.length === 1) {
    return (
      <div className="photo-carousel single" style={{ height }}>
        <img 
          src={photos[0].url} 
          alt="Goal cover" 
          className="carousel-photo"
        />
      </div>
    );
  }

  return (
    <div className="photo-carousel" style={{ height }}>
      <div 
        className="carousel-container"
        onTouchStart={handleTouchStart}
      >
        <div 
          className="carousel-track"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {photos.map((photo, index) => (
            <div key={photo.id || index} className="carousel-slide">
              <img 
                src={photo.url} 
                alt={`Goal cover ${index + 1}`}
                className="carousel-photo"
                onClick={() => onImagePress && onImagePress(photo.url)}
                style={{ cursor: onImagePress ? 'pointer' : 'default' }}
              />
            </div>
          ))}
        </div>
        
        {/* Navigation Arrows */}
        <button 
          className="carousel-arrow carousel-arrow-left"
          onClick={() => setCurrentIndex((prevIndex) => (prevIndex - 1 + photos.length) % photos.length)}
          aria-label="Previous photo"
        >
          ‹
        </button>
        <button 
          className="carousel-arrow carousel-arrow-right"
          onClick={() => setCurrentIndex((prevIndex) => (prevIndex + 1) % photos.length)}
          aria-label="Next photo"
        >
          ›
        </button>
        
        {/* Indicators */}
        {showIndicators && photos.length > 1 && (
          <div className="carousel-indicators">
            {photos.map((_, index) => (
              <button
                key={index}
                className={`carousel-indicator ${index === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Go to photo ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoCarousel;
